import { Plus, Minus } from '../lib/icons.tsx';

/**
 * − 100% + for a grid of tiles: how big the thumbnails are drawn, not what is
 * in them. Deliberately the same control the slide-zoom modal uses (same
 * classes, same gestures) so "zoom" means one thing across the studio — that
 * one magnifies ONE slide as a live frame, this one sizes a whole grid.
 *
 * The percentage is a multiplier on the grid's own base tile width, so 100%
 * always means "how it has always looked". Clicking the readout returns there.
 */
type ZoomLevelControlProps = {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  label?: string;
};

export default function ZoomLevelControl({
  value,
  onChange,
  min = 75,
  max = 300,
  step = 25,
  label = 'Thumbnail size',
}: ZoomLevelControlProps) {
  const clamp = (v: number) => Math.min(max, Math.max(min, v));
  return (
    <span className="zoom__controls" role="group" aria-label={label}>
      <button
        type="button"
        className="iconbtn"
        onClick={() => onChange(clamp(value - step))}
        disabled={value <= min}
        title="Smaller thumbnails (−)"
        aria-label="Smaller thumbnails"
      >
        <Minus />
      </button>
      <button
        type="button"
        className="zoom__level"
        onClick={() => onChange(100)}
        title="Reset thumbnail size (0)"
        aria-label={`${label} ${value} percent — click to reset`}
      >
        {value}%
      </button>
      <button
        type="button"
        className="iconbtn"
        onClick={() => onChange(clamp(value + step))}
        disabled={value >= max}
        title="Bigger thumbnails (+)"
        aria-label="Bigger thumbnails"
      >
        <Plus />
      </button>
    </span>
  );
}
